import { ref, computed, type ComputedRef } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import { doc, getDoc } from 'firebase/firestore'
import type { CustomRole } from '@/domain/types/CustomRole'
import { normalizeCustomRole } from '@/domain/types/CustomRole'
import { asCompanyRoles, customRoleIdFor, resolveUserRole } from '@/domain/types/Role'

/**
 * Rol personalizado del usuario en la compañía activa. Singleton como
 * useViewerConfig: el guard y el menú dependen de él, así que no puede haber una
 * copia ni una carga por componente. Se lee con un getDoc, sin listener (D9).
 */
const role = ref<CustomRole | null>(null)
const loaded = ref(false)
const loadedKey = ref<string | null>(null)
let pendingLoad: Promise<void> | null = null
/** Llave de la carga vigente: una respuesta tardía de otra llave se descarta. */
let activeKey: string | null = null
/** Generación de la carga vigente: cada invalidate la incrementa y las cargas viejas se descartan. */
let generation = 0

const currentCompanyId = (): string | null =>
  useCompanyStore().selectedCompanyId || localStorage.getItem('selectedCompanyId')

const keyOf = (companyId: string, roleId: string) => `${companyId}/${roleId}`

/** Id del rol personalizado del usuario de la sesión en la compañía activa (null si no tiene). */
export const sessionCustomRoleId = (): string | null => {
  const user = useAuthStore().user
  const companyId = currentCompanyId()
  if (!user || !companyId) return null
  return customRoleIdFor(
    asCompanyRoles(user.companyRoles),
    companyId,
    resolveUserRole(user.role, user.clientType)
  )
}

export const invalidateSessionCustomRole = () => {
  loadedKey.value = null
  pendingLoad = null
  activeKey = null
  generation++
  loaded.value = false
  role.value = null
}

const fetchRole = async (companyId: string, roleId: string, key: string, gen: number) => {
  let result: CustomRole | null = null
  try {
    const db = FirebaseProvider.getInstance().getFirestore()
    const snap = await getDoc(doc(db, 'Companies', companyId, 'roles', roleId))
    result = snap.exists() ? normalizeCustomRole(snap.id, snap.data()) : null
  } catch (e) {
    // Falla cerrado: sin el rol no hay permisos hasta recargar.
    console.warn('Error cargando el rol personalizado:', e)
  }
  // Respuesta tardía de una compañía/rol anterior: no pisa el estado vigente.
  if (gen !== generation) return
  role.value = result
  loadedKey.value = key
  loaded.value = true
}

/** Carga el rol una sola vez por compañía/rol; las llamadas concurrentes comparten la promesa. */
export const ensureSessionCustomRoleLoaded = async (): Promise<void> => {
  const roleId = sessionCustomRoleId()
  const companyId = currentCompanyId()
  if (!roleId || !companyId) return

  const key = keyOf(companyId, roleId)
  // También con una carga en vuelo de otra llave: no se comparte su promesa.
  if ((loadedKey.value !== null && loadedKey.value !== key) || (activeKey !== null && activeKey !== key)) {
    invalidateSessionCustomRole()
  }
  if (loaded.value && loadedKey.value === key) return

  if (!pendingLoad) {
    activeKey = key
    const load: Promise<void> = fetchRole(companyId, roleId, key, generation).finally(() => {
      if (pendingLoad === load) pendingLoad = null
    })
    pendingLoad = load
  }
  await pendingLoad
}

export function useSessionCustomRole(): {
  customRoleId: ComputedRef<string | null>
  isReady: ComputedRef<boolean>
  role: ComputedRef<CustomRole | null>
} {
  const customRoleId = computed(() => sessionCustomRoleId())
  const isReady = computed(() => {
    const id = customRoleId.value
    if (!id) return true
    return loaded.value && loadedKey.value === keyOf(currentCompanyId()!, id)
  })
  const current = computed(() => (isReady.value && customRoleId.value ? role.value : null))
  return { customRoleId, isReady, role: current }
}
